import * as Vue from 'vue';
import * as vuei18n from 'vue-i18n';
import { onClickOutside, refDebounced, useDebounceFn, useMemoize, useOnline, useWindowSize } from '@vueuse/core';
import { createHead } from '@unhead/vue/client';
import { createGtm } from '@gtm-support/vue-gtm';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { initializeInstrumentation } from '@/instrumentation';
import { registerDocumentHeadElements } from '@/utils/register-head-elements';
import auth from '@/core/auth';

import App from './App.vue';
import router from './router';
import i18n from '@/i18n';
import config from '@/config';
import { mdHtml } from '@/core/directives';

import '~/themes/default/main.scss';
import { useAuth } from '@/core';

// conditionally enable telemetry
if (+import.meta.env.VITE_ENABLE_INSTRUMENTATION) {
  console.info('Telemetry is enabled');
  initializeInstrumentation();
}

navigator.serviceWorker.addEventListener('controllerchange', () => {
  window.location.reload();
});

window.Vue = Vue;
window.VueUseCore = { onClickOutside, refDebounced, useDebounceFn, useMemoize, useWindowSize, useOnline };
window['VueI18n'] = vuei18n;

const head = createHead();
(async () => {
  const { setToken, setUser } = useAuth();

  try {
    await auth.checkToken();
    setToken();
    setUser();
  } catch (e) {
    if (e.message !== 'access token not found') {
      console.debug('User token not valid', e.message);
    } else {
      console.debug('User not available yet');
    }
  }

  const tenantConfig = await config();

  registerDocumentHeadElements();

  dayjs.extend(durationPlugin);

  try {
    const CookieModal = await import(`${import.meta.env.VITE_FBF_CUSTOM_ELEMENTS_URL}/fbf-vue-cookie-modal.es.js`);

    if (CookieModal) {
      CookieModal.registerCookieModal();
      window.CookieModal = CookieModal;
    }
  } catch (error) {
    console.error('Cookie modal:', error);
  }

  const app = Vue.createApp(App, {
    tenantConfig,
  })
    .use(head)
    .use(router)
    .directive('md-html', mdHtml)
    .use(i18n);

  if (tenantConfig.analytics?.gtmId) {
    app.use(
      createGtm({
        enabled: false,
        id: tenantConfig.analytics.gtmId,
        vueRouter: router,
        debug: !!import.meta.env.VITE_DEVELOPMENT,
      }),
    );
  }

  app.mount('#app');
})();
